import type { ReactNode } from 'react'
import { Button, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { PaymentMethodForm } from '@/features/billing/payment-method/PaymentMethodForm'
import type { PaymentMethodUpdate } from '@/features/billing/payment-method/usePaymentMethodUpdate'
import { BillingPortalLink } from './BillingPortalLink'
import type { PlanChangePaymentFailureCode } from './lib/planChange.types'

export const FailurePanel = ({
  title,
  children,
  actions,
}: {
  title: string
  children: ReactNode
  actions?: ReactNode
}): JSX.Element => (
  <div className="flex flex-col items-start gap-4 px-8 py-6">
    <Text variant={TypographyStyles.DISPLAY_XS}>{title}</Text>
    <Text variant={TypographyStyles.BODY_MD}>{children}</Text>
    {actions}
  </div>
)

interface PaymentFailurePanelProps {
  failureCode: PlanChangePaymentFailureCode | undefined
  onUpdatePaymentMethod: () => void
  onRetry: () => void
  onDismiss: () => void
}

/** An absent failure code keeps cause-neutral title copy: the web can be live against an API build that predates the field. */
export const PaymentFailurePanel = ({
  failureCode,
  onUpdatePaymentMethod,
  onRetry,
  onDismiss,
}: PaymentFailurePanelProps): JSX.Element => {
  if (failureCode === 'authentication_required') {
    return (
      <FailurePanel
        title="Your bank needs to approve this payment"
        actions={
          <div className="flex gap-3">
            <Button onClick={onUpdatePaymentMethod}>
              Use a different card
            </Button>
            <Button variant="secondary" onClick={onDismiss}>
              Back to my plan options
            </Button>
          </div>
        }
      >
        This payment needs an approval step we can&rsquo;t run here yet. Go to{' '}
        <BillingPortalLink /> to update your card there. Nothing changed on your
        plan and nothing was charged.
      </FailurePanel>
    )
  }

  return (
    <FailurePanel
      title={
        failureCode === 'card_declined'
          ? 'Your card was declined'
          : 'That payment didn’t go through'
      }
      actions={
        <div className="flex gap-3">
          <Button onClick={onUpdatePaymentMethod}>Update payment method</Button>
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        </div>
      }
    >
      Nothing changed on your plan and nothing was charged. Update your payment
      method here, then confirm again.
    </FailurePanel>
  )
}

export const PaymentMethodFormPanel = ({
  update,
  failureCode,
}: {
  update: PaymentMethodUpdate
  failureCode: PlanChangePaymentFailureCode | undefined
}): JSX.Element => (
  <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-8 py-6 [&>*]:shrink-0">
    <Text variant={TypographyStyles.DISPLAY_XS}>
      Update your payment method
    </Text>
    <Text variant={TypographyStyles.BODY_MD}>
      Nothing has been charged. Save a new card, then confirm your plan change
      again.
    </Text>
    <PaymentMethodForm
      update={update}
      flow="sheet"
      currentCard={null}
      onCancel={update.cancel}
      priorDecline={failureCode === 'card_declined'}
      footer={
        <Text variant={TypographyStyles.BODY_SM} className="text-neutral-500">
          This card becomes your default for all Heard billing, including your
          next invoice.
        </Text>
      }
    />
  </div>
)
